import React, { memo } from 'react';
import type { CSSProperties } from 'react';
import type { ActionBar as ActionBarType, StepNode } from '@segmently/schema-models/v2';
import type { TemplateVariables } from '../../../utils/contentResolver';
import type { OnActionCallback } from '../../../types/events';
import type { ButtonVisibilityState } from '../../../utils/buttonVisibility';
import { mapEdgeInsetsToCSS } from '../../../utils/styleMapper';
import { getSchemaTestId } from '../../../utils/testIdUtils';
import { useElementAnimation, mergeAnimationClassName } from '../../../animations';
import { actionBarRenders } from '../../../utils/renderPredicates';
import { Button } from '../Button';
import styles from './ActionBar.module.css';

export interface ActionBarProps {
  actionBar: ActionBarType | undefined;
  locale: string;
  variables?: TemplateVariables;
  step: StepNode;
  onAction?: OnActionCallback;
  primaryState?: ButtonVisibilityState;
  secondaryState?: ButtonVisibilityState;
  className?: string;
  'data-testid'?: string;
  editPath?: string;
}

export const ActionBar = memo(function ActionBar({
  actionBar,
  locale,
  variables,
  step,
  onAction,
  primaryState,
  secondaryState,
  className,
  'data-testid': testId,
  editPath,
}: ActionBarProps): React.ReactElement | null {
  const barAnim = useElementAnimation(editPath);

  const primaryHidden = primaryState?.hidden ?? false;
  const primaryDisabled = primaryState?.disabled ?? false;
  const secondaryHidden = secondaryState?.hidden ?? false;
  const secondaryDisabled = secondaryState?.disabled ?? false;

  // Renders nothing without a visible primary or secondary button (shared with the entrance plan:
  // utils/renderPredicates.ts)
  if (!actionBarRenders(actionBar, primaryHidden, secondaryHidden)) {
    return null;
  }

  const { primary, secondary, kind, appearance } = actionBar;

  const showPrimary = primary && !primaryHidden;
  const showSecondary = secondary && !secondaryHidden;

  const isHorizontal = kind === 'inline';

  const containerStyle: CSSProperties = {
    ...mapEdgeInsetsToCSS(appearance?.insets),
    ...(isHorizontal && { flexDirection: 'row' }),
    ...barAnim.style,
  };

  // In Horizontal mode, secondary (left) button gets percentage width
  const secondaryStyle: CSSProperties = isHorizontal && appearance?.leftButtonWidthPercentage
    ? { flex: `0 0 ${appearance.leftButtonWidthPercentage}%` }
    : {};

  const primaryStyle: CSSProperties = isHorizontal && appearance?.leftButtonWidthPercentage
    ? { flex: '1' }
    : {};

  const schemaTestId = testId ?? getSchemaTestId(editPath);

  // In vertical mode, render Button directly (no wrapper div) so that
  // Button's wrapper becomes a direct flex child of ActionBar.
  // This allows alignSelf:'stretch' (fullWidth) to work with align-items:center.
  // In horizontal mode, keep wrapper divs for flex sizing (percentage width).
  const renderButton = (
    tapTarget: typeof primary,
    wrapperStyle: CSSProperties,
    disabled: boolean,
    suffix: string,
  ) => {
    const btn = (
      <Button
        tapTarget={tapTarget}
        locale={locale}
        variables={variables}
        step={step}
        onAction={onAction}
        disabled={disabled}
        editPath={editPath ? `${editPath}.${suffix}` : undefined}
      />
    );
    return isHorizontal ? <div style={wrapperStyle}>{btn}</div> : btn;
  };

  return (
    <footer
      className={mergeAnimationClassName(
        className ? `${styles.actionBar} ${className}` : styles.actionBar,
        barAnim,
      )}
      style={containerStyle}
      data-anim={barAnim['data-anim']}
      data-testid={schemaTestId}
      data-edit-path={editPath}
    >
      {/* Vertical: primary on top, secondary below (matches editor panel order).
           Horizontal: secondary (left), primary (right) — flex row order. */}
      {isHorizontal ? (
        <>
          {showSecondary && renderButton(secondary!, secondaryStyle, secondaryDisabled, 'secondary')}
          {showPrimary && renderButton(primary!, primaryStyle, primaryDisabled, 'primary')}
        </>
      ) : (
        <>
          {showPrimary && renderButton(primary!, primaryStyle, primaryDisabled, 'primary')}
          {showSecondary && renderButton(secondary!, secondaryStyle, secondaryDisabled, 'secondary')}
        </>
      )}
    </footer>
  );
});

ActionBar.displayName = 'ActionBar';
